/**
 * Initialize Braintree Drop-in with donation form integration
 *
 * @param {Object} config Configuration object
 * @param {string} config.clientToken - Braintree client authorization token
 * @param {string} config.container - CSS selector for the Braintree container
 * @param {string} config.siteName - Site name for Apple Pay display
 * @param {string} config.googlePayMerchantId - Google Pay merchant ID
 * @param {jQuery} config.$form - jQuery form element
 * @returns {void}
 */
window.initBraintreeDonation = function({
    clientToken,
    container = '#subscribe_payment',
    siteName,
    googlePayMerchantId,
    $form
}) {
    if (typeof braintree === 'undefined') {
        console.error('Braintree library not loaded');
        return;
    }

    braintree.dropin.create({
        authorization: clientToken,
        container: container,
        paypal: {
            flow: 'vault'
        },
        // venmo: {},
        applePay: {
            displayName: siteName,
            paymentRequest: {
                total: {
                    label: 'Donation',
                    amount: '0.00'
                },
                // We recommend collecting billing address information, at minimum
                // billing postal code, and passing that billing postal code with all
                // Apple Pay transactions as a best practice.
                requiredBillingContactFields: ["postalAddress"]
            }
        },
        // paypalCredit: {
        //   flow: 'vault'
        // }
        googlePay: {
            googlePayVersion: 2,
            merchantId: googlePayMerchantId,
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPrice: '0.00',
                currencyCode: 'USD'
            },
            allowedPaymentMethods: [{
                type: 'CARD',
                parameters: {
                    // We recommend collecting and passing billing address information with all Google Pay transactions as a best practice.
                    billingAddressRequired: true,
                    billingAddressParameters: {
                        format: 'FULL'
                    }
                }
            }]
        }
    }, function (createErr, instance) {
        if (createErr) {
            console.error('Braintree Drop-in error:', createErr);
            return;
        }

        // Store instance globally for payment configuration updates
        window.brtree = instance;

        // Update UI text
        $('[data-braintree-id="choose-a-way-to-pay"]').html('Select a payment method');
        $('.subscribe-action').show();

        // Notify Alpine components that brtree is ready so they can push initial totals
        window.dispatchEvent(new CustomEvent('brtree:ready'));

        // Hook into payment method events to auto-submit for external payment methods
        instance.on('paymentMethodRequestable', function(event) {
            // This fires when a payment method is available (card filled out, or external payment completed)
            // For external payment methods (Apple Pay, Google Pay, PayPal), the type will be set
            // and we should auto-submit since the user has completed the external flow
            if ((event.type === 'ApplePayCard' || event.type === 'GooglePayCard' || event.type === 'AndroidPayCard') && event.paymentMethodIsSelected === true) {
                // Check if Turnstile is valid before auto-submitting
                var turnstileResponse = $('input[name="cf-turnstile-response"]').val();
                if (!turnstileResponse || turnstileResponse.length === 0) {
                    var turnstileGroup = $('input[name="cf-turnstile-response"]').closest('.form-group')[0];
                    if (turnstileGroup) {
                        turnstileGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    return;
                }

                // Auto-submit the form for external payment methods
                instance.requestPaymentMethod(function (err, payload) {
                    if (!err) {
                        window.dispatchEvent(new CustomEvent('external-payment-submit'));
                        console.log('External payment option confirmed. Processing payment...');

                    }
                });
            }
        });

        instance.on('paymentOptionSelected', function(event) {
            // This fires when user selects a payment option
            // We can use this to track what payment method they're attempting to use
            console.log('Payment option selected:', event.paymentOption);
        });
    });
};

/**
 * Donation Widget Component for Alpine.js
 *
 * Provides a reusable donation form widget with support for:
 * - One-time and recurring donations
 * - Preset and custom amounts
 * - Apple Pay and Google Pay integration via Braintree
 * - Dynamic payment total updates
 * - Form validation
 *
 * @param {Object} config Configuration object
 * @param {number} config.initialIsRecurring - Initial recurring donation state (0 or 1)
 * @param {string} config.initialPlan - Initial recurring plan ('monthly', 'quarterly', 'annually')
 * @param {string} config.initialAmount - Initial donation amount or 'custom'
 * @param {number} config.initialCustomValue - Initial custom amount value
 * @param {number} config.initialProjectId - Initial project ID (optional)
 * @param {string} config.initialDonateNote - Initial donation note (optional)
 * @param {string} config.initialMagazineDonateNote - Initial magazine donation note (optional)
 * @param {boolean} config.initialShowDesignation - Show designation fields initially (optional)
 * @param {number} config.initialSubscribingToMagazine - Initial magazine subscription state (optional)
 * @param {Object} config.initialErrors - Initial validation errors from server (optional)
 * @returns {Object} Alpine.js component data
 */
window.donationWidget = function({
    initialIsRecurring,
    initialPlan,
    initialAmount,
    initialCustomValue,
    initialProjectId = 0,
    initialDonateNote = '',
    initialMagazineDonateNote = '',
    initialShowDesignation = false,
    initialSubscribingToMagazine = 0,
    initialErrors = {}
}) {
    /**
     * Update Braintree payment method configurations with new amount
     * @param {number} amt - Amount to update
     */
    function updatePaymentConfigurations(amt) {
        if (window.brtree && amt > 0) {
            // Apple Pay
            window.brtree.updateConfiguration('applePay', 'paymentRequest', {
                total: {
                    label: 'Donation',
                    amount: amt.toFixed(2)
                }
            });
            // Google Pay
            window.brtree.updateConfiguration('googlePay', 'transactionInfo', {
                totalPriceStatus: 'FINAL',
                totalPrice: amt.toFixed(2),
                currencyCode: 'USD'
            });
        }
    }

    return {
        // Donation configuration
        is_recurring: Number(initialIsRecurring) === 1 ? 1 : 0,
        showRecurringAlert: false,
        plan: initialPlan || 'monthly',
        amount: (initialAmount && `${initialAmount}`.length ? `${initialAmount}` : '20'),
        customValue: initialCustomValue,

        // UI toggles
        showDesignation: Boolean(initialShowDesignation),
        subscribingToMagazine: Number(initialSubscribingToMagazine) === 1 ? 1 : 0,

        // Additional fields
        projectId: Number(initialProjectId) || 0,
        donateNote: initialDonateNote || '',
        magazineDonateNote: initialMagazineDonateNote || '',

        // Validation errors (merged with server-side errors)
        errors: {
            amount: initialErrors.amount || '',
            project: initialErrors.project || '',
            donateNote: initialErrors.donateNote || ''
        },

        /**
         * Select custom amount option and focus the input
         */
        selectCustom() {
            this.amount = 'custom';
            this.$nextTick(() => {
                if (this.$refs.customValue) this.$refs.customValue.focus();
            });
        },

        /**
         * Get the currently selected amount as a number
         * @returns {number} Selected amount
         */
        selectedAmount() {
            if (this.amount === 'custom') {
                const n = parseFloat(this.customValue);
                return isNaN(n) ? 0 : n;
            }
            const n = parseFloat(this.amount);
            return isNaN(n) ? 0 : n;
        },

        /**
         * Initialize the component
         */
        init() {
            // Run once on init with initial values
            const initialAmt = this.amount === 'custom'
                ? (parseFloat(this.customValue) || 0)
                : (parseFloat(this.amount) || 0);
            updatePaymentConfigurations(initialAmt);

            // Also run once when Braintree Drop-in becomes ready
            window.addEventListener('brtree:ready', () => {
                const amt = this.amount === 'custom'
                    ? (parseFloat(this.customValue) || 0)
                    : (parseFloat(this.amount) || 0);
                updatePaymentConfigurations(amt);
            }, { once: true });

            // Watch preset/custom amount selection and update Braintree totals
            this.$watch('amount', () => {
                const amt = this.amount === 'custom'
                    ? (parseFloat(this.customValue) || 0)
                    : (parseFloat(this.amount) || 0);
                updatePaymentConfigurations(amt);
            });

            // Also watch changes to the custom value when in custom mode
            this.$watch('customValue', () => {
                if (this.amount !== 'custom') return;
                const amt = parseFloat(this.customValue) || 0;
                updatePaymentConfigurations(amt);
            });

            window.addEventListener('external-payment-submit', () => {
                this.validateAndSubmit();
            });

            // Note: Magazine subscription checkbox field requirement toggling is handled
            // in the page-specific jQuery code, as the checkbox may be outside this
            // Alpine.js component's scope for layout reasons.
            // If your checkbox is inside the Alpine component, uncomment this:

            // this.$watch('subscribingToMagazine', (value) => {
            //     const addressFields = ['country', 'first_name', 'last_name', 'address1', 'city', 'zip'];
            //     addressFields.forEach(fieldName => {
            //         const field = document.querySelector(`[name="${fieldName}"]`);
            //         if (field) {
            //             field.required = value === 1;
            //         }
            //     });
            // });

            // // Set initial state of address field requirements
            // const addressFields = ['country', 'first_name', 'last_name', 'address1', 'city', 'zip'];
            // addressFields.forEach(fieldName => {
            //     const field = document.querySelector(`[name="${fieldName}"]`);
            //     if (field) {
            //         field.required = this.subscribingToMagazine === 1;
            //     }
            // });
        },

        /**
         * Validate form and submit if valid
         */
        validateAndSubmit() {
            this.errors.amount = '';
            if (this.amount === 'custom') {
                const val = parseFloat(this.customValue);
                if (!val || val < 5) {
                    this.errors.amount = 'Please enter a valid custom amount (minimum $5).';
                    this.$nextTick(() => this.$refs.customValue && this.$refs.customValue.focus());
                    return;
                }
            }

            // If validation passes, request payment method from Braintree and submit
            if (window.brtree) {
                window.brtree.requestPaymentMethod((err, payload) => {
                    if (!err) {
                        document.getElementById('payment_nonce').value = payload.nonce;
                        console.log('Payment method nonce:', payload.nonce);
                        const button = document.querySelector('.btn-donate');
                        if (button) {
                            button.textContent = button.dataset.loadingText || 'Processing...';
                            button.disabled = true;
                        }
                        document.getElementById('subscribe_form').submit();
                    }
                });
            } else {
                // If Braintree isn't initialized, submit anyway (shouldn't happen in normal flow)
                console.log('Braintree not initialized, debugging needed');
                // this.$refs.form.submit();
            }
        }
    };
};
